/**
 * ThemePicker — header button that opens a listbox of the five themes.
 * Keyboard: arrows move, Enter/Space picks, Escape closes; focus returns
 * to the button.
 */
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { IoCheckmark, IoColorPaletteSharp } from 'react-icons/io5'
import { useTheme } from './ThemeProvider'
import { THEMES } from './themes'

const optionId = (id) => `sc-theme-option-${id}`

const Swatch = ({ colors }) => (
  <span aria-hidden="true" className="flex h-4 w-7 shrink-0 overflow-hidden rounded border border-line">
    {colors.map((c) => (
      <span key={c} className="flex-1" style={{ background: c }} />
    ))}
  </span>
)

Swatch.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
}

const ThemePicker = () => {
  const { theme, setTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(theme)
  const buttonRef = useRef(null)
  const listRef = useRef(null)
  const wrapRef = useRef(null)
  const current = THEMES.find((t) => t.id === theme) || THEMES[0]

  useEffect(() => {
    if (!open) return undefined
    setActive(theme)
    listRef.current?.focus()
    const onPointer = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open, theme])

  const close = () => {
    setOpen(false)
    buttonRef.current?.focus()
  }

  const choose = (id) => {
    setTheme(id)
    close()
  }

  const onKeyDown = (e) => {
    const index = THEMES.findIndex((t) => t.id === active)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActive(THEMES[(index + step + THEMES.length) % THEMES.length].id)
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      setActive(THEMES[e.key === 'Home' ? 0 : THEMES.length - 1].id)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      choose(active)
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      close()
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Theme: ${current.label}`}
        title={`Theme: ${current.label}`}
        className="header-button text-white hover:text-chrome-accent"
      >
        <IoColorPaletteSharp className="icon text-xl" aria-hidden="true" />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Theme"
          tabIndex={-1}
          aria-activedescendant={optionId(active)}
          onKeyDown={onKeyDown}
          className="absolute right-0 top-full z-[10000] mt-2 w-44 rounded-lg border border-line bg-surface p-1 text-content shadow-lg focus:outline-none"
        >
          {THEMES.map((t) => {
            const selected = t.id === theme
            return (
              <li
                key={t.id}
                id={optionId(t.id)}
                role="option"
                aria-selected={selected}
                onClick={() => choose(t.id)}
                onMouseEnter={() => setActive(t.id)}
                className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                  t.id === active ? 'bg-surface-2' : ''
                } ${selected ? 'font-semibold' : ''}`}
              >
                <Swatch colors={t.swatch} />
                <span className="flex-1">{t.label}</span>
                {selected && <IoCheckmark aria-hidden="true" className="text-accent" />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default ThemePicker
